import type { RoadmapCompanyQuestion, RoadmapItem, RoadmapSubtask } from "../types/roadmap";
import { ROADMAP_ASSISTANT_SCHEMA } from "./roadmapAssistantConstants";
import { invokeRoadmap } from "./roadmapApi";

export { ROADMAP_ASSISTANT_SCHEMA } from "./roadmapAssistantConstants";

export type RoadmapExportOptions = {
  /** When false, working notes are omitted (locked roadmap admin view). */
  includeWorkingNotes: boolean;
};

export type AssistantPasteSubtaskV1 = {
  id?: string;
  /** Required for new rows (no `id`). Omit when updating existing rows if only `done` / `expectedFinishAt` change. */
  title?: string;
  done?: boolean;
  expectedFinishAt?: string | null;
};

/** Merge by `id`, or create new rows without `id`. */
export type AssistantPasteCompanyQuestionV1 = {
  id?: string;
  question?: string;
  resolved?: boolean;
};

/** Payload the assistant returns; only present fields are applied */
export type AssistantPastePayloadV1 = {
  schema: typeof ROADMAP_ASSISTANT_SCHEMA;
  itemId: string;
  workingNotes?: string;
  expectedFinishAt?: string | null;
  subtasks?: AssistantPasteSubtaskV1[];
  removeSubtaskIds?: string[];
  companyQuestions?: AssistantPasteCompanyQuestionV1[];
  removeCompanyQuestionIds?: string[];
};

export type AssistantExportSnapshotV1 = AssistantPastePayloadV1 & {
  title: string;
  status: string;
  description?: string;
};

/** Slug from task title for a readable local filename (ASCII-ish). */
function slugifyRoadmapTitle(title: string): string {
  const ascii = title
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  const slug = ascii
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
  return slug || "task";
}

/**
 * Suggested path for a **local-only** markdown scratchpad (folder is gitignored).
 * Includes a short title slug + id fragment so it is easy to spot in the editor tab list.
 */
export function suggestRoadmapLocalScratchPath(item: { id: string; title: string }): string {
  const shortId = item.id.replace(/-/g, "").slice(0, 8);
  return `.roadmap-local/${slugifyRoadmapTitle(item.title)}-${shortId}.md`;
}

export function buildAssistantBundleMarkdown(
  item: RoadmapItem,
  subtasks: RoadmapSubtask[],
  companyQuestions: RoadmapCompanyQuestion[],
  options: RoadmapExportOptions
): string {
  const { includeWorkingNotes } = options;

  const sortedCompanyQs = [...companyQuestions].sort((a, b) => a.sort_order - b.sort_order);

  const snapshot: AssistantExportSnapshotV1 = {
    schema: ROADMAP_ASSISTANT_SCHEMA,
    itemId: item.id,
    title: item.title,
    status: item.status,
    description: item.description || undefined,
    expectedFinishAt: item.expected_finish_at ?? null,
    subtasks: subtasks.map((s) => ({
      id: s.id,
      title: s.title,
      done: s.done,
      expectedFinishAt: s.expected_finish_at ?? null,
    })),
    companyQuestions: sortedCompanyQs.map((q) => ({
      id: q.id,
      question: q.question,
      resolved: q.resolved,
    })),
  };
  if (includeWorkingNotes) {
    snapshot.workingNotes = item.working_notes;
  }

  const json = JSON.stringify(snapshot, null, 2);

  const appliedFieldsLine = includeWorkingNotes
    ? "Applied fields (if present): `workingNotes`, `expectedFinishAt`, `subtasks` (merge by `id` or create new without `id`), `removeSubtaskIds`, `companyQuestions` (same), `removeCompanyQuestionIds`."
    : "Applied fields (if present): `expectedFinishAt`, `subtasks`, `companyQuestions`, `removeSubtaskIds`, `removeCompanyQuestionIds`. Unlock official edit to use `workingNotes`.";

  const workingNotesGuidance = includeWorkingNotes
    ? [
        "### Good `workingNotes` (for the JSON you paste back)",
        "Write notes so **anyone (or a future chat) can resume without re-reading the code**:",
        "- **Done this session:** what changed in one short paragraph.",
        "- **Where:** important file paths, feature flags, migrations, env vars.",
        "- **Decisions:** trade-offs, things we skipped on purpose.",
        "- **Next / open:** what is still TODO, blockers, or questions for the team.",
        "",
        "The assistant should **replace or extend** `workingNotes` in the JSON with this kind of content after implementing work.",
        "",
      ]
    : [];

  const localScratchPath = suggestRoadmapLocalScratchPath(item);

  return [
    "## Roadmap task (for Cursor / assistant)",
    "",
    "**Give this whole message to your assistant.** It has a human summary plus a JSON snapshot.",
    "",
    "When the assistant should **write updates back** into the app, ask it to reply with **one** `json` code block using schema `" +
      ROADMAP_ASSISTANT_SCHEMA +
      "` and the **same `itemId`**. You paste that reply into the task → **Apply assistant paste**.",
    "",
    "### Local scratch file for **this task only** (not committed)",
    "Create or update **`" +
      localScratchPath +
      "`** (folder `.roadmap-local/` at repo root is **gitignored**, so nothing there is pushed to GitHub).",
    "",
    "Use **one file per roadmap task** so tabs and search stay clear. The path above includes a **short slug from the title** plus an **id fragment** so you can recognize it in the editor.",
    "",
    "Start the file with the **human title** and roadmap id, then your notes, checklists, and open questions:",
    "",
    "```",
    "# " + item.title,
    "itemId: `" + item.id + "`",
    "```",
    "",
    "Mirror anything that should survive for the team into **`workingNotes`** (in the JSON you paste back), not only into `" +
      localScratchPath +
      "`.",
    "",
    appliedFieldsLine,
    "Official fields (title, status) are **not** changed by paste — use Official edit + secret.",
    "",
    ...workingNotesGuidance,
    "### Summary",
    `- **Title:** ${item.title}`,
    `- **Status:** ${item.status}`,
    item.description ? `- **Description:** ${item.description}` : null,
    `- **Expected finish:** ${item.expected_finish_at ?? "_(none)_"}`,
    "",
    ...(includeWorkingNotes
      ? ["### Working notes", item.working_notes || "_(empty)_", ""]
      : []),
    "### Questions for company",
    sortedCompanyQs.length === 0
      ? "_(none)_"
      : sortedCompanyQs
          .map((q) => {
            return `- [${q.resolved ? "x" : " "}] ${q.question} \`id:${q.id}\``;
          })
          .join("\n"),
    "",
    "### Sub-todos",
    subtasks.length === 0
      ? "_(none)_"
      : subtasks
          .map((s) => {
            const due = s.expected_finish_at ? ` due:${s.expected_finish_at}` : "";
            return `- [${s.done ? "x" : " "}] ${s.title}${due} \`id:${s.id}\``;
          })
          .join("\n"),
    "",
    "### JSON snapshot (`" + ROADMAP_ASSISTANT_SCHEMA + "`)",
    "",
    "```json",
    json,
    "```",
    "",
  ]
    .filter(Boolean)
    .join("\n");
}

export function extractAssistantPasteJson(text: string): unknown | null {
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fenced) {
    try {
      return JSON.parse(fenced[1].trim());
    } catch {
      return null;
    }
  }
  const t = text.trim();
  if (t.startsWith("{")) {
    try {
      return JSON.parse(t);
    } catch {
      return null;
    }
  }
  return null;
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}

export function parseAssistantPastePayload(
  raw: unknown,
  expectedItemId: string
): { ok: true; payload: AssistantPastePayloadV1 } | { ok: false; error: string } {
  if (!isRecord(raw)) {
    return { ok: false, error: "Paste is not a JSON object." };
  }
  if (raw.schema !== ROADMAP_ASSISTANT_SCHEMA) {
    return { ok: false, error: `Missing or wrong schema (need "${ROADMAP_ASSISTANT_SCHEMA}").` };
  }
  if (typeof raw.itemId !== "string" || raw.itemId !== expectedItemId) {
    return { ok: false, error: "itemId must match this task (wrong task or corrupted paste)." };
  }

  const payload: AssistantPastePayloadV1 = {
    schema: ROADMAP_ASSISTANT_SCHEMA,
    itemId: raw.itemId,
  };

  if ("workingNotes" in raw) {
    if (raw.workingNotes === null) {
      payload.workingNotes = "";
    } else if (typeof raw.workingNotes === "string") {
      payload.workingNotes = raw.workingNotes;
    } else {
      return { ok: false, error: "workingNotes must be string, null, or omitted." };
    }
  }

  if ("expectedFinishAt" in raw) {
    if (raw.expectedFinishAt === null) {
      payload.expectedFinishAt = null;
    } else if (typeof raw.expectedFinishAt === "string") {
      payload.expectedFinishAt = raw.expectedFinishAt;
    } else {
      return { ok: false, error: "expectedFinishAt must be string, null, or omitted." };
    }
  }

  if ("removeSubtaskIds" in raw) {
    if (!Array.isArray(raw.removeSubtaskIds)) {
      return { ok: false, error: "removeSubtaskIds must be an array of strings." };
    }
    const ids = raw.removeSubtaskIds.filter((x): x is string => typeof x === "string");
    if (ids.length !== raw.removeSubtaskIds.length) {
      return { ok: false, error: "removeSubtaskIds must contain only strings." };
    }
    payload.removeSubtaskIds = ids;
  }

  if ("subtasks" in raw) {
    if (!Array.isArray(raw.subtasks)) {
      return { ok: false, error: "subtasks must be an array." };
    }
    const list: AssistantPasteSubtaskV1[] = [];
    for (const row of raw.subtasks) {
      if (!isRecord(row)) {
        return { ok: false, error: "Each subtask must be an object." };
      }
      const id = typeof row.id === "string" ? row.id : undefined;
      const title =
        typeof row.title === "string" ? row.title.trim() : undefined;
      const done = typeof row.done === "boolean" ? row.done : undefined;
      let expectedFinishAt: string | null | undefined;
      if ("expectedFinishAt" in row) {
        if (row.expectedFinishAt === null || typeof row.expectedFinishAt === "string") {
          expectedFinishAt = row.expectedFinishAt;
        } else {
          return { ok: false, error: "subtask.expectedFinishAt must be string or null." };
        }
      }

      if (id) {
        if (title !== undefined && title === "") {
          return { ok: false, error: "subtask.title cannot be empty when provided." };
        }
        if (title === undefined && done === undefined && expectedFinishAt === undefined) {
          return {
            ok: false,
            error:
              "Each subtask with id needs a non-empty title, and/or done, and/or expectedFinishAt (omit title only when updating done or date).",
          };
        }
        const st: AssistantPasteSubtaskV1 = { id };
        if (title !== undefined) st.title = title;
        if (done !== undefined) st.done = done;
        if (expectedFinishAt !== undefined) st.expectedFinishAt = expectedFinishAt;
        list.push(st);
      } else {
        if (!title) {
          return {
            ok: false,
            error: "New subtasks (no id) need a non-empty title string.",
          };
        }
        const st: AssistantPasteSubtaskV1 = { title };
        if (done !== undefined) st.done = done;
        if (expectedFinishAt !== undefined) st.expectedFinishAt = expectedFinishAt;
        list.push(st);
      }
    }
    payload.subtasks = list;
  }

  if ("removeCompanyQuestionIds" in raw) {
    if (!Array.isArray(raw.removeCompanyQuestionIds)) {
      return { ok: false, error: "removeCompanyQuestionIds must be an array of strings." };
    }
    const ids = raw.removeCompanyQuestionIds.filter((x): x is string => typeof x === "string");
    if (ids.length !== raw.removeCompanyQuestionIds.length) {
      return { ok: false, error: "removeCompanyQuestionIds must contain only strings." };
    }
    payload.removeCompanyQuestionIds = ids;
  }

  if ("companyQuestions" in raw) {
    if (!Array.isArray(raw.companyQuestions)) {
      return { ok: false, error: "companyQuestions must be an array." };
    }
    const list: AssistantPasteCompanyQuestionV1[] = [];
    for (const row of raw.companyQuestions) {
      if (!isRecord(row)) {
        return { ok: false, error: "Each company question must be an object." };
      }
      const id = typeof row.id === "string" ? row.id : undefined;
      const question =
        typeof row.question === "string" ? row.question.trim() : undefined;
      const resolved = typeof row.resolved === "boolean" ? row.resolved : undefined;

      if (id) {
        if (question !== undefined && question === "") {
          return { ok: false, error: "companyQuestions: question cannot be empty when provided." };
        }
        if (question === undefined && resolved === undefined) {
          return {
            ok: false,
            error: "companyQuestions: entries with id need question and/or resolved.",
          };
        }
        const entry: AssistantPasteCompanyQuestionV1 = { id };
        if (question !== undefined) entry.question = question;
        if (resolved !== undefined) entry.resolved = resolved;
        list.push(entry);
      } else {
        if (!question) {
          return { ok: false, error: "New company questions need a non-empty question string." };
        }
        const entry: AssistantPasteCompanyQuestionV1 = { question };
        if (resolved !== undefined) entry.resolved = resolved;
        list.push(entry);
      }
    }
    payload.companyQuestions = list;
  }

  const hasWork =
    payload.workingNotes !== undefined ||
    payload.expectedFinishAt !== undefined ||
    (payload.removeSubtaskIds && payload.removeSubtaskIds.length > 0) ||
    (payload.subtasks && payload.subtasks.length > 0) ||
    (payload.removeCompanyQuestionIds && payload.removeCompanyQuestionIds.length > 0) ||
    (payload.companyQuestions && payload.companyQuestions.length > 0);

  if (!hasWork) {
    return {
      ok: false,
      error:
        "Nothing to apply (add workingNotes, expectedFinishAt, subtasks, companyQuestions, removeSubtaskIds, or removeCompanyQuestionIds).",
    };
  }

  return { ok: true, payload };
}

export async function applyAssistantPastePayload(
  payload: AssistantPastePayloadV1,
  refresh: () => Promise<void>
): Promise<{ ok: true } | { ok: false; error: string }> {
  const { itemId } = payload;

  try {
    if (payload.removeSubtaskIds?.length) {
      for (const sid of payload.removeSubtaskIds) {
        await invokeRoadmap({ action: "delete_subtask", subtaskId: sid });
      }
    }

    if (payload.removeCompanyQuestionIds?.length) {
      for (const qid of payload.removeCompanyQuestionIds) {
        await invokeRoadmap({ action: "delete_company_question", questionId: qid });
      }
    }

    if (payload.workingNotes !== undefined) {
      await invokeRoadmap({
        action: "update_working_notes",
        itemId,
        workingNotes: payload.workingNotes,
      });
    }

    if ("expectedFinishAt" in payload) {
      await invokeRoadmap({
        action: "update_expected_finish",
        itemId,
        expectedFinishAt: payload.expectedFinishAt ?? null,
      });
    }

    if (payload.companyQuestions?.length) {
      for (const cq of payload.companyQuestions) {
        if (cq.id) {
          const body: Record<string, unknown> = {
            action: "update_company_question",
            questionId: cq.id,
          };
          if (cq.question !== undefined) body.question = cq.question;
          if (cq.resolved !== undefined) body.resolved = cq.resolved;
          await invokeRoadmap(body);
        } else if (cq.question !== undefined) {
          const res = await invokeRoadmap({
            action: "add_company_question",
            itemId,
            question: cq.question,
          });
          const newId = typeof res.id === "string" ? res.id : undefined;
          if (newId && cq.resolved === true) {
            await invokeRoadmap({
              action: "update_company_question",
              questionId: newId,
              resolved: true,
            });
          }
        }
      }
    }

    if (payload.subtasks?.length) {
      for (const st of payload.subtasks) {
        if (st.id) {
          const body: Record<string, unknown> = {
            action: "update_subtask",
            subtaskId: st.id,
          };
          if (st.title !== undefined) body.title = st.title;
          if (typeof st.done === "boolean") body.done = st.done;
          if ("expectedFinishAt" in st) {
            body.expectedFinishAt = st.expectedFinishAt ?? null;
          }
          await invokeRoadmap(body);
        } else {
          const title = st.title;
          if (!title) {
            return { ok: false, error: "New subtask missing title." };
          }
          const res = await invokeRoadmap({
            action: "add_subtask",
            itemId,
            title,
          });
          const newId = typeof res.id === "string" ? res.id : undefined;
          if (
            newId &&
            (typeof st.done === "boolean" || "expectedFinishAt" in st)
          ) {
            const body: Record<string, unknown> = {
              action: "update_subtask",
              subtaskId: newId,
            };
            if (typeof st.done === "boolean") body.done = st.done;
            if ("expectedFinishAt" in st) {
              body.expectedFinishAt = st.expectedFinishAt ?? null;
            }
            if (Object.keys(body).length > 2) {
              await invokeRoadmap(body);
            }
          }
        }
      }
    }

    await refresh();
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : "Apply failed" };
  }
}
